import { useTranslation } from 'react-i18next';
import React, { memo, useCallback, useMemo, useState } from 'react';
import cx from 'clsx';

import { IconRcVoteCreator } from '@/assets/proposals';
import { DesktopHeader2, WiderTable } from '@/components';
import { useDeviceTypeIsMobile } from '@/hooks';
import { openNewWindow, useCurrentUserId } from '@/utils';

import { ProposalVote, ProposalStatus, ProposalComment } from '../components';
import { useProposalDetail, useVoters } from '../hooks';
import { getPcColumns, getMobileColumns } from './columns';
import style from './index.module.scss';
import { CustomTitle } from '@/components/CustomTitle';
import { ShareBtn } from '../components/ShareBtn';

const PAGE_SIZE = 100;

const Container = memo(({ id }: { id: string }) => {
  const { t } = useTranslation();

  const isMobile = useDeviceTypeIsMobile();
  const { data } = useProposalDetail(id);
  const [start, setStart] = useState(0);
  const { data: voterRes, refetch, isLoading, isError } = useVoters(id, start);
  const userId = useCurrentUserId();

  const columns = useMemo(
    () => (isMobile ? getMobileColumns(userId) : getPcColumns(refetch)),
    [refetch, isMobile, userId],
  );

  const result = useMemo(() => {
    return voterRes?.voters;
  }, [voterRes?.voters]);

  const handleRowClick = useCallback(
    row => {
      if (isMobile) {
        return;
      }

      openNewWindow(`/profile/${row.id}`, '_blank');
    },
    [isMobile],
  );

  const pagination = useMemo(
    () => ({
      current: Math.floor(start / PAGE_SIZE) + 1,
      defaultPageSize: PAGE_SIZE,
      onChange: (page: number) => {
        setStart(PAGE_SIZE * (page - 1));
      },
      total: voterRes?.total_count,
      showQuickJumper: true,
      showSizeChanger: false,
    }),
    [voterRes?.total_count, start, setStart],
  );

  const titlePrefix = useMemo(() => {
    if (!data) return '';
    return `${data?.description}(Vote${data?.voter_count})-Vote|more`;
  }, [data]);

  console.log('start', start);

  return (
    <div className={cx(style.container, isMobile && style.mobile)}>
      <CustomTitle titlePrefix={titlePrefix} />
      {isMobile ? null : <DesktopHeader2 title={t('DetailContainer.1la16')} enableBack />}
      <div className={style.proposalMain}>
        {data ? (
          <>
            <ProposalVote
              data={data}
              className={style.proposalVoteIcon}
              onVote={refetch}
              isLogged={!!userId}
              source="detail_page"
            />
            <div className={style.proposalBody}>
              <div className={style.proposalBodyLeft}>
                <div className={style.voteTitle}>{data.description}</div>
                <div className={style.voteDetail}>
                  {data?.url_list?.length ? (
                    <>
                      <div>{t('DetailContainer.4aj268')}</div>
                      {data?.url_list?.map(x => (
                        <div>
                          <a
                            className={style.relatedLink}
                            href={x}
                            target="_blank"
                            rel="noreferrer">
                            {x}
                          </a>
                        </div>
                      ))}
                    </>
                  ) : null}
                  {data?.addr_list?.length ? (
                    <>
                      <div className={style.relatedAddress}>{t('DetailContainer.j3i2bf')}</div>
                      {data?.addr_list?.map(x => <div>{x}</div>)}
                    </>
                  ) : null}
                  {data?.issue_addr ? (
                    <>
                      <div className={style.relatedAddress}>{t('DetailContainer.u904c2')}</div>
                      <div>{data?.issue_addr}</div>
                    </>
                  ) : null}
                </div>
                <div className={style.voteCreator}>
                  <IconRcVoteCreator className={style.voterIcon} />
                  <a href={`/profile/${data.creator_id}`} target="_blank" rel="noreferrer">
                    {t('DetailContainer.1jmakkv')}
                    <span className={style.voteCreatorId}>{data.creator_id}</span>
                  </a>
                </div>
                <ProposalStatus className={style.proposalStatus} data={data} />
                <ProposalComment comment={data?.reply_msg} className={style.proposalComment} />
              </div>
              {!isMobile ? <ShareBtn id={Number(id)} /> : null}
            </div>
          </>
        ) : null}
      </div>
      <WiderTable
        className={style.table}
        data={result}
        showHeader={!isMobile}
        pagination={pagination}
        columns={columns}
        showReload
        loading={isLoading}
        onRowClick={handleRowClick}
        isError={isError}
        reload={refetch}
      />
    </div>
  );
});

export default Container;
